import {validatePaperExport} from './paperEdit.js';
import {sequenceClips} from './sequence.js';
import {sourceColourOrder,sourcePremiereLabel} from './storyContext.js';
import {assemblySectionMarkers} from './sectionMarkers.js';

const escapeXml = value => String(value ?? '').replace(/[&<>"']/g, character => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;'
})[character]);

const frame = (seconds, project) => Math.round(seconds * project.fpsNum / project.fpsDen);
const rate = project => `<rate><timebase>${Math.round(project.fpsNum / project.fpsDen)}</timebase><ntsc>${project.fpsDen === 1001 ? 'TRUE' : 'FALSE'}</ntsc></rate>`;
// A semicolon denotes SMPTE drop-frame timecode at an NTSC fractional rate.
// Never rewrite the user's original source timecode when creating a proxy.
const tc = project => {
 const sourceTimecode=project.timecode || '00:00:00:00';
 const drop=typeof sourceTimecode==='string' && sourceTimecode.includes(';') &&
  project.fpsDen===1001 && [30000,60000].includes(project.fpsNum);
 return `<timecode>${rate(project)}<string>${escapeXml(sourceTimecode)}</string><displayformat>${drop?'DF':'NDF'}</displayformat></timecode>`;
};

export function buildXml(project) {
  const paper=project.paperExport;
  if(paper)validatePaperExport(paper);
  if(paper?.sequenceRate)project={...project,...paper.sequenceRate};
  const sources=paper ? paper.blocks.map(b=>paper.sources[b.sourceId]) : [project];
  for(const source of sources){
    if(!source || !source.filename || !['duration','width','height','fpsNum','fpsDen','audioSampleRate'].every(key=>Number.isFinite(source[key]) && source[key]>0) || !Number.isInteger(source.audioChannels) || source.audioChannels<0 || source.audioChannels>16){
      throw new Error('Cannot export: source video information is missing or invalid. Check Source settings for each interview.');
    }
  }
  const intervals = (paper ? paper.blocks : sequenceClips(project))
    .map(part => {const source=paper ? paper.sources[part.sourceId] : project;return {source,sourceId:part.sourceId || '1',inFrame:frame(part.start,source),outFrame:frame(part.end,source)};})
    ;
  if(!intervals.length)throw new Error('Nothing to export. Add a passage or restore a clip first.');
  if(intervals.some(part=>!Number.isFinite(part.inFrame)||!Number.isFinite(part.outFrame)||part.inFrame<0||part.outFrame<=part.inFrame||part.outFrame>frame(part.source.duration,part.source))){
    throw new Error('Cannot export: a clip is outside its source video or shorter than one frame. Adjust the passage boundaries.');
  }
  let cursor = 0, elapsed = 0;
  const segments = intervals.map((part, index) => {
    elapsed+=(part.outFrame-part.inFrame)*part.source.fpsDen/part.source.fpsNum;
    const end=frame(elapsed,project);
    if(end<=cursor)throw new Error('A passage is shorter than one sequence frame. Extend its boundaries before exporting.');
    const segment = { ...part, start: cursor, end, index: index + 1 };
    cursor = segment.end;
    return segment;
  });
  // Use the same stable interview-to-label mapping as the paper-edit UI.
  // Premiere looks up these label names in the editor's current label preset.
  const colourOrder=sourceColourOrder(paper?.sourceColourOrder,
    paper ? Object.keys(paper.sources) : ['1']);
  const sourceLabels=new Map(colourOrder.map(id=>[id,sourcePremiereLabel(id,colourOrder)]));
  const markers=paper?assemblySectionMarkers(paper,segments.map(part=>part.start),cursor)
    .map(marker=>`<marker><name>${escapeXml(marker.title)}</name><in>${marker.frame}</in><out>${marker.frame}</out></marker>`).join(''):'';
  const definedFiles=new Set();
  const clip = (segment, type, channel) => {
    const source=segment.source;
    const filename=escapeXml(source.filename),sourceDuration=frame(source.duration,source);
    const fileId=`source-${escapeXml(segment.sourceId)}`;
    const file=definedFiles.has(fileId) ? `<file id="${fileId}"/>` : `<file id="source-${escapeXml(segment.sourceId)}"><name>${filename}</name><pathurl>file://localhost/${escapeXml(encodeURIComponent(source.filename))}</pathurl><duration>${sourceDuration}</duration>${rate(source)}${tc(source)}<media><video><samplecharacteristics>${rate(source)}<width>${source.width}</width><height>${source.height}</height><anamorphic>FALSE</anamorphic><pixelaspectratio>square</pixelaspectratio><fielddominance>none</fielddominance></samplecharacteristics></video><audio><samplecharacteristics><depth>16</depth><samplerate>${source.audioSampleRate}</samplerate></samplecharacteristics><channelcount>${source.audioChannels}</channelcount></audio></media></file>`;
    definedFiles.add(fileId);
    const id = `${type}-${segment.index}-${channel}`;
    const links = [`video-${segment.index}-1`, ...Array.from({ length: source.audioChannels }, (_, i) => `audio-${segment.index}-${i + 1}`)];
    return `<clipitem id="${id}"><enabled>TRUE</enabled><name>${filename}</name><duration>${sourceDuration}</duration>${rate(source)}<start>${segment.start}</start><end>${segment.end}</end><in>${segment.inFrame}</in><out>${segment.outFrame}</out>${file}${type === 'audio' ? `<sourcetrack><mediatype>audio</mediatype><trackindex>${channel}</trackindex></sourcetrack>` : ''}${links.map((link, i) => `<link><linkclipref>${link}</linkclipref><mediatype>${i === 0 ? 'video' : 'audio'}</mediatype><trackindex>${i === 0 ? 1 : i}</trackindex><clipindex>${i===0 ? segment.index : segments.filter(s=>s.index<=segment.index && s.source.audioChannels>=i).length}</clipindex></link>`).join('')}<labels><label2>${sourceLabels.get(segment.sourceId) || sourcePremiereLabel(segment.sourceId,colourOrder)}</label2></labels></clipitem>`;
  };
  const video = segments.map(segment => clip(segment, 'video', 1)).join('');
  const audio = Array.from({ length: Math.max(project.audioChannels,...segments.map(s=>s.source.audioChannels)) }, (_, index) =>
    `<track>${segments.filter(segment=>segment.source.audioChannels>index).map(segment => clip(segment, 'audio', index + 1)).join('')}</track>`
  ).join('');
  return `<?xml version="1.0" encoding="UTF-8"?>\n<!DOCTYPE xmeml>\n<xmeml version="4"><sequence id="sequence-1"><name>${escapeXml(project.title)}</name><duration>${cursor}</duration>${rate(project)}${tc(project)}${markers}<media><video><format><samplecharacteristics>${rate(project)}<width>${project.width}</width><height>${project.height}</height><anamorphic>FALSE</anamorphic><pixelaspectratio>square</pixelaspectratio><fielddominance>none</fielddominance></samplecharacteristics></format><track>${video}</track></video><audio><format><samplecharacteristics><depth>16</depth><samplerate>${project.audioSampleRate}</samplerate></samplecharacteristics></format>${audio}</audio></media></sequence></xmeml>`;
}

const srtTime = seconds => {
  const ms = Math.round(seconds * 1000);
  return `${String(Math.floor(ms / 3600000)).padStart(2, '0')}:${String(Math.floor(ms / 60000) % 60).padStart(2, '0')}:${String(Math.floor(ms / 1000) % 60).padStart(2, '0')},${String(ms % 1000).padStart(3, '0')}`;
};

export function buildText(project) {
  const intervals = project.paperExport?.blocks || sequenceClips(project);
  return intervals.map(part => (project.paperExport ? project.paperExport.sources[part.sourceId].words : project.words)
    .filter(word => word.start < part.end && word.end > part.start)
    .map(word => word.text).join(' ')).filter(Boolean).join('\n');
}

export function buildSrt(project) {
  const intervals = project.paperExport?.blocks || sequenceClips(project);
  const cues = [];
  let offset = 0;
  for (const part of intervals) {
    const words = (project.paperExport ? project.paperExport.sources[part.sourceId].words : project.words).filter(word => word.start < part.end && word.end > part.start);
    const groups = [];
    for (const word of words) {
      const last = groups[groups.length - 1];
      if (last && last.line === word.line) last.words.push(word);
      else groups.push({ line: word.line, words: [word] });
    }
    for (const group of groups) {
      const first = group.words[0], last = group.words[group.words.length - 1];
      cues.push(`${cues.length + 1}\n${srtTime(offset + Math.max(first.start, part.start) - part.start)} --> ${srtTime(offset + Math.min(last.end, part.end) - part.start)}\n${group.words.map(word => word.text).join(' ')}`);
    }
    offset += part.end - part.start;
  }
  return cues.join('\n\n') + (cues.length ? '\n' : '');
}

export function downloadBlob(name, blob) {
  if(!(blob instanceof Blob))throw new Error('There is no rendered audio to download.');
  const url=URL.createObjectURL(blob);
  const anchor=document.createElement('a');
  anchor.href=url;
  anchor.download=name;
  anchor.click();
  setTimeout(()=>URL.revokeObjectURL(url),60000);
}
export function downloadFile(name, contents, type) {
  const url = URL.createObjectURL(new Blob([contents], { type }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

